import React, { useCallback, useEffect, useState } from 'react';
import type { UserRole } from '../modules/users/types/users.types';
import { authApiService } from '../modules/auth/services/auth.api';
import {
  AuthContext,
  type AuthUser,
} from './auth-context';
import {
  getAccessToken,
  getRefreshToken,
  getPermissions,
  setAccessToken,
  setPermissions,
  setRefreshToken,
  setUserFields,
  getUserId,
  getEmail,
  getName,
  getRole,
  getUserImage,
  clearTokens,
  isAccessTokenExpired,
} from '../core/auth/token';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const updateCurrentUser = useCallback((updates: Partial<AuthUser>) => {
    setUser((currentUser) => {
      if (!currentUser) return currentUser;

      const nextUser = {
        ...currentUser,
        ...updates,
      };

      setUserFields({
        user_id: nextUser.id,
        email: nextUser.email,
        name: nextUser.name,
        role: nextUser.role,
        user_image: getUserImage(),
      });

      return nextUser;
    });
  }, []);

  useEffect(() => {
    const restoreSession = async () => {
      const accessToken = getAccessToken();

      if (!accessToken) {
        setIsLoading(false);
        return;
      }

      if (!isAccessTokenExpired()) {
        const id = getUserId();
        const email = getEmail();
        const name = getName();
        const role = getRole() as UserRole | null;
        const permissions = getPermissions();

        if (id && email && name && role) {
          setUser({ id, email, name, role, permissions });
          setIsLoading(false);
          return;
        }
      }

      const storedRefreshToken = getRefreshToken();
      if (!storedRefreshToken) {
        clearTokens();
        setIsLoading(false);
        return;
      }

      try {
        const res = await authApiService.refreshToken(storedRefreshToken);

        if (!res.isSuccess) {
          throw new Error(res.message ?? 'Refresh failed');
        }

        setAccessToken(res.response.access_token);
        setRefreshToken(res.response.refresh_token);

        const id = getUserId();
        const email = getEmail();
        const name = getName();
        const role = getRole() as UserRole | null;
        const permissions = getPermissions();

        if (id && email && name && role) {
          setUser({ id, email, name, role, permissions });
        } else {
          clearTokens();
        }
      } catch {
        clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    void restoreSession();
  }, []);

  const login = useCallback(async (credentials: {
    username: string;
    password: string;
  }) => {
    setIsLoading(true);
    try {
      const res = await authApiService.requestLoginOtp({
        username: credentials.username,
        password: credentials.password,
      });

      if (!res.isSuccess) {
        throw new Error(res.message ?? 'Login failed.');
      }

      const userId = res.response?.id;

      if (!userId) {
        throw new Error('VSAC request succeeded but user_id was missing.');
      }

      return {
        userId,
        message: res.message ?? 'VSAC sent to registered email',
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyOtp = useCallback(async (payload: {
    userId: string;
    otp: string;
  }) => {
    setIsLoading(true);
    try {
      const res = await authApiService.verifyOtp({
        user_id: payload.userId,
        otp: payload.otp,
      });

      if (!res.isSuccess || !res.response) {
        throw new Error(res.message ?? 'VSAC verification failed.');
      }

      const { user: apiUser, access_token, refresh_token } = res.response;

      setAccessToken(access_token);
      setRefreshToken(refresh_token);

      const roleName = apiUser.role.name;
      const permissions = apiUser.role?.permissions ?? [];

      setUserFields({
        user_id: apiUser.id,
        email: apiUser.email,
        name: apiUser.name,
        role: roleName,
        user_image: apiUser.user_image,
      });
      setPermissions(permissions);

      setUser({
        id: apiUser.id,
        email: apiUser.email,
        name: apiUser.name,
        role: roleName as UserRole,
        permissions,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resendOtp = useCallback(async (payload: {
    userId: string;
  }) => {
    const res = await authApiService.resendOtp({ user_id: payload.userId });

    if (!res.isSuccess) {
      throw new Error(res.message ?? 'Resend VSAC failed.');
    }

    return { message: res.message ?? 'VSAC resent successfully' };
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApiService.logout();
    } catch {
      // Server-side logout failure is non-critical; clear locally regardless
    } finally {
      clearTokens();
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin: user?.role?.toLowerCase() === 'admin',
        permissions: user?.permissions ?? [],
        isLoading,
        login,
        verifyOtp,
        resendOtp,
        logout,
        updateCurrentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
